import React, { useEffect, useRef, useState, useMemo } from "react";
import Chart from "react-apexcharts";

function ItemChart({ itemIdSelected, currentUser }) {
  const [timeRange, setTimeRange] = useState("7D");
  const [chartData, setChartData] = useState({ "7D": [], "30D": [], "90D": [] });
  const [ohlcData, setOhlcData] = useState({ "7D": [], "30D": [], "90D": [] });
  const [itemName, setItemName] = useState(""); // Track item name dynamically
  const [loading, setLoading] = useState(false);
  const [showCandles, setShowCandles] = useState(false);
  const [debouncedItemId, setDebouncedItemId] = useState(itemIdSelected);

  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const reqSeqRef = useRef(0);

  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (!itemIdSelected) {
      setDebouncedItemId(undefined);
      return;
    }
    const t = setTimeout(() => setDebouncedItemId(itemIdSelected), 250);
    return () => clearTimeout(t);
  }, [itemIdSelected]);

  useEffect(() => {
    if (!debouncedItemId) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const mySeq = ++reqSeqRef.current;
    setLoading(true);

    (async () => {
      try {
        const token = await currentUser.getIdToken(false);
        const res = await fetch("https://getitemcharts-5dhfwyjvzq-uc.a.run.app", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ itemId: debouncedItemId }),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (mySeq !== reqSeqRef.current || !mountedRef.current) return;

        setItemName(data.itemName || data.itemName || "UNKNOWN ITEM");

        setChartData({
          "7D": data.itemChart7D || [],
          "30D": data.itemChart30D || [],
          "90D": data.itemChart90D || [],
        });
        setOhlcData({
          "7D": data.ohlcData7D || [],
          "30D": data.ohlcData30D || [],
          "90D": data.ohlcData90D || [],
        });
      } catch (err) {
        if (err.name !== "AbortError") console.error("Chart fetch error:", err);
      } finally {
        if (mountedRef.current && mySeq === reqSeqRef.current) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [debouncedItemId, currentUser]);

  const rawPoints = useMemo(() => chartData[timeRange] || [], [chartData, timeRange]);

  const totalChange = useMemo(() => {
    if (loading || rawPoints.length < 2) return null;
    const first = Number(rawPoints[0].Price || 0);
    const last = Number(rawPoints[rawPoints.length - 1].Price || 0);
    const diff = last - first;
    return { diff, percent: first !== 0 ? (diff / first) * 100 : 0 };
  }, [rawPoints, loading]);

  const mapped = useMemo(() => {
    return rawPoints.map((d) => ({
      x: d.x || d.Date,
      Price: round2(d.Price),
      Volume: round2(d.Volume || 0),
    }));
  }, [rawPoints]);

  const maxVolumeVal = useMemo(() => {
    if (!mapped.length) return 100;
    const maxVal = Math.max(...mapped.map((d) => d.Volume));
    return maxVal > 0 ? maxVal : 100;
  }, [mapped]);

  const series = useMemo(() => {
    if (loading || (!mapped.length && !ohlcData[timeRange]?.length)) return [];

    if (showCandles) {
      return [
        {
          name: "Candles",
          type: "candlestick",
          data: (ohlcData[timeRange] || []).map((d) => ({
            x: d.x || d.Date,
            y: [round2(d.open), round2(d.high), round2(d.low), round2(d.close)],
          })),
        },
      ];
    }

    return [
      {
        name: "Market Price",
        type: "line",
        data: mapped.map((d) => ({ x: d.x, y: d.Price })),
      },
      {
        name: "Volume",
        type: "bar",
        data: mapped.map((d) => ({ x: d.x, y: d.Volume })),
      },
    ];
  }, [loading, mapped, showCandles, ohlcData, timeRange]);

  const options = useMemo(() => {
    const primaryYAxis = {
      seriesName: showCandles ? "Candles" : "Market Price",
      labels: { 
        formatter: (val) => Number(val || 0).toFixed(1), 
        style: { colors: "#50e3c2", fontSize: '9px' },
        offsetX: -10 
      },
      axisBorder: { show: true, color: "#50e3c2" },
      title: { text: "Price", style: { color: "#50e3c2", fontSize: "9px", fontFamily: "JetBrains Mono" } }
    };

    const secondaryYAxis = {
      opposite: true,
      seriesName: "Volume",
      max: maxVolumeVal * 4,
      labels: { 
        formatter: (val) => Math.floor(Number(val || 0)).toLocaleString(), 
        style: { colors: "rgba(255,255,255,0.4)", fontSize: '9px' },
        offsetX: -10 
      },
      axisBorder: { show: true, color: "rgba(255,255,255,0.2)" },
      title: { text: "Volume", style: { color: "rgba(255,255,255,0.4)", fontSize: "9px", fontFamily: "JetBrains Mono" } }
    };

    return {
      chart: { 
        type: showCandles ? "candlestick" : "line", 
        background: "transparent", 
        toolbar: { show: false }, 
        animations: { enabled: false }
      },
      colors: showCandles ? [] : ["#50e3c2", "rgba(255, 255, 255, 0.12)"],
      legend: { 
        show: !showCandles, 
        position: 'top',
        horizontalAlign: 'center',
        fontSize: '10px',
        fontFamily: 'JetBrains Mono, monospace',
        labels: { colors: "#fff" },
        markers: { width: 8, height: 8, strokeWidth: 0, fillOpacity: 1 },
        itemMargin: { horizontal: 5, vertical: 2 }
      },
      xaxis: { 
        type: "category", 
        labels: { 
          style: { colors: "#fff", fontSize: '9px', cssClass: 'apexcharts-xaxis-label' },
          rotate: -45,
          maxHeight: 30
        },
        axisBorder: { show: false },
        tooltip: { enabled: false }
      },
      yaxis: showCandles ? [primaryYAxis] : [primaryYAxis, secondaryYAxis],
      tooltip: { 
        theme: "dark", 
        shared: true,
        intersect: false,
        y: {
          formatter: (val, { seriesIndex, dataPointIndex, w }) => {
            if (!showCandles && seriesIndex === 1) {
              return val !== undefined && val !== null ? `${Math.floor(val).toLocaleString()} units` : "---";
            }

            if (showCandles && seriesIndex === 0) {
              try {
                const o = w.globals.seriesCandleO[seriesIndex][dataPointIndex];
                const h = w.globals.seriesCandleH[seriesIndex][dataPointIndex];
                const l = w.globals.seriesCandleL[seriesIndex][dataPointIndex];
                const c = w.globals.seriesCandleC[seriesIndex][dataPointIndex];
                return `O: $${o.toFixed(2)} H: $${h.toFixed(2)} L: $${l.toFixed(2)} C: $${c.toFixed(2)}`;
              } catch (e) {
                return "---";
              }
            }

            return val !== undefined && val !== null ? `$${val.toFixed(2)}` : "---";
          }
        }
      },
      grid: { 
        borderColor: "#333",
        padding: { left: 10, right: 10, top: 0, bottom: 0 } 
      },
      stroke: { 
        width: showCandles ? 1 : [2, 0], 
        curve: 'smooth' 
      },
      plotOptions: {
        bar: {
          columnWidth: "60%",
        },
        candlestick: {
          colors: {
            upward: '#4caf50',
            downward: '#f44336'
          },
          wick: { useFillColor: true }
        }
      },
      states: {
        active: { filter: { type: 'none' } },
        hover: { filter: { type: 'none' } },
        inactive: { opacity: 0.25 }
      }
    };
}, [showCandles, maxVolumeVal]);

  return (
    <div style={{ width: "100%", padding: "4px", boxSizing: "border-box" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "6px" }}>
        <h2 style={{ color: "#50e3c2", margin: 0, fontSize: "0.85rem", textTransform: "uppercase", fontFamily: "JetBrains Mono, monospace" }}>
          {debouncedItemId ? (
            <>
              <span style={{ color: "#fff", marginRight: "6px" }}>{itemName}</span>
              <span style={{ opacity: 0.5 }}>(${debouncedItemId})</span>
            </>
          ) : (
            "LOADING..."
          )}
        </h2>
        {totalChange && (
          <div style={{ color: totalChange.diff >= 0 ? "#4caf50" : "#f44336", fontSize: "0.65rem", fontFamily: "JetBrains Mono, monospace" }}>
            {totalChange.diff >= 0 ? "+" : ""}{round2(totalChange.diff)} ({round2(totalChange.percent)}%)
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: "4px", marginBottom: "10px", width: "100%" }}>
        {["7D", "30D", "90D"].map((range) => (
          <button
            key={range}
            className={`jetbrains-btn ${timeRange === range ? "" : "disabled"}`}
            style={{
              flex: 1,
              background: timeRange === range ? "rgba(80, 227, 194, 0.2)" : "rgba(0,0,0,0.2)",
              color: timeRange === range ? "#50e3c2" : "#888",
              border: `1px solid ${timeRange === range ? "#50e3c2" : "#444"}`,
              padding: "6px 0",
              fontSize: "0.6rem",
              cursor: "pointer",
              borderRadius: "2px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "24px"
            }}
            onClick={() => setTimeRange(range)}
          >
            {range}
          </button>
        ))}
        <button
          className="jetbrains-btn"
          style={{
            flex: 1.5,
            background: "rgba(0,0,0,0.2)",
            color: "#50e3c2",
            border: "1px solid #50e3c2",
            padding: "6px 0", 
            fontSize: "0.6rem",
            cursor: "pointer",
            borderRadius: "2px",
            minHeight: "24px"
          }}
          onClick={() => setShowCandles((prev) => !prev)}
        >
          {showCandles ? "LINE" : "CANDLE"}
        </button>
      </div>

      {loading || series.length === 0 ? (
        <div style={{ color: "#50e3c2", textAlign: "center", padding: "40px", fontSize: "0.6rem" }}>
          LOADING...
        </div>
      ) : (
        <div style={{ marginLeft: "-15px" }}>
          <Chart 
            key={`${debouncedItemId}-${timeRange}-${showCandles ? 'candle' : 'line'}`}
            options={options} 
            series={series} 
            type={showCandles ? "candlestick" : "line"} 
            height={400} 
          />
        </div>
      )}
    </div>
  );
}

export default React.memo(ItemChart);